<script lang="ts">
export default {
  inheritAttrs: false,
}
</script>

<script setup lang="ts">
import type {HTMLAttributes} from 'vue'
import {Primitive, type PrimitiveProps} from 'radix-vue'
import {type ButtonVariants, buttonVariants} from '.'
import {cn} from '@/lib/utils'
import {Link} from '@inertiajs/vue3'

interface Props extends PrimitiveProps {
  variant?: ButtonVariants['variant']
  size?: ButtonVariants['size']
  as?: string
  class?: HTMLAttributes['class']
  href?: string
  confirm?: boolean,
  icon?: string
}

const props = withDefaults(defineProps<Props>(), {
  as: 'button',
})
</script>

<template>
  <AlertDialog v-if="confirm">
    <AlertDialogTrigger :class="cn(buttonVariants({ variant, size }), props.class)">
      <Icon v-if="icon" :name="icon" class="mr-2 size-4" />
      <slot></slot>
    </AlertDialogTrigger>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
        <AlertDialogDescription>
          This action cannot be undone. This will permanently delete your account
          and remove your data from our servers.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>Cancel</AlertDialogCancel>
        <AlertDialogAction
          :as="Link"
          :class="buttonVariants({ 'variant': 'destructive', size: 'default' })"
          v-bind="$attrs"
          :href="href"
        >
          Continue
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
  <Primitive
    v-else
    :as="(!!href && as === 'button') ? Link : as"
    :as-child="asChild"
    :class="cn(buttonVariants({ variant, size }), props.class)"
    v-bind="$attrs"
    :href="href"
  >
    <Icon v-if="icon" :name="icon" class="mr-2 size-4" />
    <slot/>
  </Primitive>
</template>
